/**
 * useNodeModels Hook
 *
 * Fetches AI model configurations by node type, brand, and purpose.
 * Uses built-in React state management with in-memory caching.
 *
 * @example
 * ```tsx
 * // All models for a node type
 * const { data: models = [], isLoading } = useNodeModels('image');
 *
 * // Models for specific brand and purpose
 * const { data: models = [], isLoading } = useNodeModels('image', 'flux', 'create');
 * const defaultModel = getDefaultModel(models);
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { AINodeType, NodeModelConfig, AiModelPurpose } from '@genieartinc/schemas';
import { modelRegistryAPI } from '@/lib/api/model-registry';

export interface UseNodeModelsResult {
  data: NodeModelConfig[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// In-memory cache for model configs with TTL (per nodeType + brand + purpose + subPurpose)
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const modelCache = new Map<string, CacheEntry<NodeModelConfig[]>>();
const EMPTY_MODELS: NodeModelConfig[] = [];

function getCached(key: string): NodeModelConfig[] | null {
  const entry = modelCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    modelCache.delete(key);
    return null;
  }
  return entry.data;
}

function setCache(key: string, data: NodeModelConfig[]): void {
  modelCache.set(key, { data, timestamp: Date.now() });
}

function getCacheKey(
  nodeType: AINodeType,
  brand?: string,
  purpose?: AiModelPurpose,
  subPurpose?: string
): string {
  return `${nodeType}:${brand || 'all'}:${purpose || 'all'}:${subPurpose || 'all'}`;
}

/**
 * Warm the model cache without triggering React state updates.
 * Call from non-component code (e.g., prefetch utilities).
 */
export async function warmModelCache(
  nodeType: AINodeType,
  brand?: string,
  purpose?: AiModelPurpose,
  subPurpose?: string
): Promise<NodeModelConfig[]> {
  const cacheKey = getCacheKey(nodeType, brand, purpose, subPurpose);
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const models = brand
      ? await modelRegistryAPI.getModelsByFamily(nodeType, brand, purpose, subPurpose)
      : await modelRegistryAPI.getModelsByNodeType(nodeType, purpose, subPurpose);
    setCache(cacheKey, models);
    return models;
  } catch {
    return [];
  }
}

/**
 * Fetch AI models for a specific node type, brand, purpose, and subPurpose
 */
export function useNodeModels(
  nodeType: AINodeType,
  brand?: string,
  purpose?: AiModelPurpose,
  subPurpose?: string,
  enabled = true
): UseNodeModelsResult {
  const cacheKey = getCacheKey(nodeType, brand, purpose, subPurpose);
  const requestId = useRef(0);
  const [result, setResult] = useState(() => ({
    key: cacheKey,
    data: getCached(cacheKey) ?? EMPTY_MODELS,
    isLoading: enabled && getCached(cacheKey) === null,
    error: null as Error | null,
  }));

  const fetchModels = useCallback(async (force = false) => {
    const id = ++requestId.current;
    if (!enabled) return;
    const cached = force ? null : getCached(cacheKey);
    if (cached) {
      setResult({ key: cacheKey, data: cached, isLoading: false, error: null });
      return;
    }
    setResult({ key: cacheKey, data: EMPTY_MODELS, isLoading: true, error: null });

    try {
      const models = brand
        ? await modelRegistryAPI.getModelsByFamily(nodeType, brand, purpose, subPurpose)
        : await modelRegistryAPI.getModelsByNodeType(nodeType, purpose, subPurpose);

      if (id !== requestId.current) return;
      setCache(cacheKey, models);
      setResult({ key: cacheKey, data: models, isLoading: false, error: null });
    } catch (err) {
      if (id !== requestId.current) return;
      setResult({ key: cacheKey, data: EMPTY_MODELS, isLoading: false, error: err instanceof Error ? err : new Error('Unknown error') });
    }
  }, [nodeType, brand, purpose, subPurpose, cacheKey, enabled]);

  useEffect(() => {
    void fetchModels();
    return () => { requestId.current += 1; };
  }, [fetchModels]);
  const refetch = useCallback(() => { void fetchModels(true); }, [fetchModels]);

  const current = result.key === cacheKey ? result : { data: EMPTY_MODELS, isLoading: true, error: null };
  return {
    data: enabled ? current.data : EMPTY_MODELS,
    isLoading: enabled && current.isLoading,
    error: enabled ? current.error : null,
    refetch,
  };
}

/**
 * Get the default model from a list of models
 */
export function getDefaultModel(models: NodeModelConfig[]): NodeModelConfig | null {
  return models.find((m) => m.isDefault) ?? models[0] ?? null;
}

/**
 * Find a model by ID
 */
export function findModelById(
  models: NodeModelConfig[],
  modelId: string
): NodeModelConfig | null {
  return models.find((m) => m.id === modelId)
    ?? models.find((m) => m.endpointId === modelId)
    ?? null;
}
